import { useRef, useState, useCallback, useEffect } from "react";

export const useAmbientSound = () => {
  const [enabled, setEnabled] = useState(false);
  const ctxRef = useRef(null);
  const gainRef = useRef(null);

  const stop = useCallback(() => {
    const ctx = ctxRef.current;
    const gain = gainRef.current;
    if (ctx && gain) {
      gain.gain.cancelScheduledValues(ctx.currentTime);
      gain.gain.setValueAtTime(gain.gain.value, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.7);
      setTimeout(() => ctx.close().catch(() => {}), 800);
    }
    ctxRef.current = null;
    gainRef.current = null;
  }, []);

  const start = useCallback(() => {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const master = ctx.createGain();
    master.gain.value = 0;
    master.connect(ctx.destination);
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 520;
    filter.connect(master);
    [110, 164.81, 220].forEach((f, i) => {
      const o = ctx.createOscillator();
      o.type = "sine";
      o.frequency.value = f;
      o.detune.value = i * 5 - 4;
      const g = ctx.createGain();
      g.gain.value = 0.3;
      o.connect(g);
      g.connect(filter);
      o.start();
    });
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.08;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 160;
    lfo.connect(lfoGain);
    lfoGain.connect(filter.frequency);
    lfo.start();
    master.gain.linearRampToValueAtTime(0.045, ctx.currentTime + 2.5);
    ctxRef.current = ctx;
    gainRef.current = master;
  }, []);

  const toggle = useCallback(() => {
    setEnabled((prev) => {
      if (prev) stop();
      else start();
      return !prev;
    });
  }, [start, stop]);

  useEffect(() => () => stop(), [stop]);

  return { enabled, toggle };
};
